import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import {
    HasProduct,
    ProductContext,
    ProductContextModel,
    withProduct,
} from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import {
    getProductInfoFromList,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { ImageModel, ProductModel, SpecificationModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import ProductDescription from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductPrimaryImage from "@insite/content-library/Components/ProductPrimaryImage";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import { ProductDetailsPrimaryImageStyles } from "@insite/content-library/Widgets/ProductDetails/ProductDetailsPrimaryImage";
import ProductListActions from "@insite/content-library/Widgets/ProductList/ProductListActions";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridWrapper } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconMemo, IconProps } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import File from "@insite/mobius/Icons/File";
import Plus from "@insite/mobius/Icons/Plus";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import Tab, { TabPresentationProps } from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getContrastColor from "@insite/mobius/utilities/getContrastColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
    attributesPosition = "attributesPosition",
    documentsPosition = "documentsPosition",
    documentsTitle = "documentsTitle",
    excludedSpecifications = "excludedSpecifications",
    showRelatedProducts = "showRelatedProducts",
    relatedProductsTitle = "relatedProductsTitle",
    relatedProductType = "relatedProductType",
    toolsused = "toolsused",
    relatedToolsUsedTitle = "toolsUsedTitle",
    relatedSubsituteType = "relatedSubsituteType",
    relatedSubstituteTitle = "relatedSubstituteTitle",
    substitute = "substitute",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showDocuments]: boolean;
        [fields.showAttributes]: boolean;
        [fields.attributesPosition]: string;
        [fields.documentsPosition]: string;
        [fields.documentsTitle]: string;
        [fields.excludedSpecifications]: string;
        [fields.showRelatedProducts]: boolean;
        [fields.relatedProductsTitle]: string;
        [fields.relatedProductType]: string;
        [fields.relatedToolsUsedTitle]: string;
        [fields.toolsused]: string;
        [fields.relatedSubstituteTitle]: string;
        [fields.substitute]: string;
    };
}

const carouselType = "relatedProducts";
const carouselTypeToolsUsedWidgetId = "carouselTypeToolUsedWidgetId";
const carouselTypeSubstitutesWidgetId = "carouselTypeSubstitutesWidgetId";
const seedWithManuallyAssigned = "";
const displayProductsFrom = "allCategories";
const selectedCategoryIds: string[] = [];
const numberOfProductsToDisplay = 10;
let tabs: JSX.Element[] = [];
let currentTab = null;
const tabsModel: ProductDetailSepcificationTabs = {};

const mapStateToProps = (state: ApplicationState, widgetProps: WidgetProps) => {
    const relatedProducts = getProductsForProductInfoList(state, widgetProps.id);
    const relatedProductContextList = relatedProducts?.map(
        product =>
            ({
                product,
                productInfo: getProductInfoFromList(state, widgetProps.id, product.id),
            } as ProductContextModel),
    );

    const relatedTools = getProductsForProductInfoList(state, carouselTypeToolsUsedWidgetId);
    const relatedToolsContextList = relatedTools?.map(
        product =>
            ({
                product,
                productInfo: getProductInfoFromList(state, carouselTypeToolsUsedWidgetId, product.id),
            } as ProductContextModel),
    );

    const substitutes = getProductsForProductInfoList(state, carouselTypeSubstitutesWidgetId);
    const relatedSubstitutesList = substitutes?.map(
        product =>
            ({
                product,
                productInfo: getProductInfoFromList(state, carouselTypeSubstitutesWidgetId, product.id),
            } as ProductContextModel),
    );

    const isLoaded =
        relatedProductContextList !== undefined &&
        relatedToolsContextList !== undefined &&
        relatedSubstitutesList !== undefined;

    return {
        relatedProducts: getProductsForProductInfoList(state, widgetProps.id),
        productSettings: getSettingsCollection(state).productSettings,
        pageType: getCurrentPage(state).type,
        relatedProductContextList,
        relatedToolsContextList,
        relatedSubstitutesList,
        isLoaded,
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
    updateProductInfo,
};

type Props = OwnProps &
    WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProduct;

export interface ProductDetailsSpecificationsStyles {
    specificationsAccordion?: AccordionProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    documentTypeText?: TypographyPresentationProps;
    documentTypeIcon?: LazyImageProps;
    documentTypeNameContainer?: InjectableCss;
    cardList?: CardListStyles;
    tabStyle?: TabPresentationProps;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    specificationsAccordion: {
        headingLevel: 2,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
    specificationsAccordionSection: {
        headerProps: {
            css: css`
                button {
                    background: #636262 0% 0% no-repeat padding-box;
                }
            `,
        },
        titleTypographyProps: {
            color: "primary.contrast",
        },
        toggleIconProps: {
            color: "primary.contrast",
            src: Plus,
            size: 26,
        },
        collapseIconProps: {
            color: "primary.contrast",
            size: 26,
        },
        expandIconProps: {
            color: "primary.contrast",
            src: Plus,
            size: 26,
        },
    },
    documentTypeText: {
        css: css`
            vertical-align: super;
        `,
    },
    documentLink: {
        typographyProps: {
            css: css`
                display: flex;
            `,
        },
    },
    documentTypeIcon: {
        css: css`
            background-color: ${getColor("common.background")};
            padding-right: 8px;
            & > img {
                object-fit: scale-down;
                height: 100%;
            }
        `,
        height: "60px",
        width: "60px",
    },
    documentTypeNameContainer: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    cardList: {
        gridContainer: {
            gap: 0,
            css: css``,
            offsetProps: { style: { width: "100%" } },
        },
    },
    tabStyle: {
        css: css`
            cursor: pointer;
            border:1px solid #016EAF;
            border-bottom: 1px solid #cccccc;
            margin-right: 10px;
            background:#f7f7f7;
            &[aria-selected="true"]{
                background: ${getColor("primary")};
                color: ${getContrastColor("primary")};
            }
            &:hover {
                border: 1px solid #016EAF;
            }
            &:focus {
                border-color:transparent;
            }
        `,
    }
};

export interface RelatedProductAccordionStyles {
    gridContainer?: GridContainerProps;
    moreLessGridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
}

const relatedProductAccordionStyles: RelatedProductAccordionStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            min-width: 320px; /* prevent overlap with second column */
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 10px 0;
            &:last-child {
                border-bottom: none;
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    css`
                        padding-top:15px;
                        padding-bottom:15px;
                    `,
                    css`
                        padding-top:15px;
                        padding-bottom:15px;
                    `,
                    null,
                    null,
                ],
                "minMax",
            )}
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            padding-right: 20px;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
    },
    infoGridItem: {
        width: [12, 12, 12, 7, 7],
        css: css`
            flex-direction: column;
        `,
    },
    actionsGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            align-items: center;
        `,
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    moreLessGridContainer:{
        css: css`
            border:1px solid ${getColor("primary")};
            background:#f7f7f7;
        `
    }
};

const primaryImageStyles: ProductDetailsPrimaryImageStyles = {
    fullPrimaryImageModal: {
        cssOverrides: {
            modalContainer: css`
                width: auto;
            `,
            modalBody: css`
                width: auto;
                max-width: initial;
                max-height: initial !important;
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 10px 10px 0;
            `,
            modalContent: css`
                text-align: center;
            `,
        },
    },
    fullPrimaryImage: {
        image: {
            css: css`
                img {
                    width: auto;
                    max-height: 70vh;
                }
            `,
        },
    },
    primaryImage: {
        centeringWrapper: {
            css: css`
                min-height: 110px;
                width:110px;
                height:110px;
            `,
        },
        img: {
            css: css`
                width: 100%;
                height: 100%;
                object-fit: contain;
                object-position: center top;
            `,
        },
    },
};

function createProductTab(
    productListContext: ProductContextModel[] | undefined,
    productTitle: string,
    productTabName: string,
    showFullList = false,
    showFullListToggleFunction: (value: boolean) => void,
): any {
    if (!productListContext || productListContext.length <= 0) {
        return null;
    }
    const tabHeadLine = `${productTitle} (${productListContext.length})`;

    let productsToRender = [];

    if (!showFullList) {
        productsToRender = productListContext.slice(0, 2);
    } else {
        productsToRender = productListContext;
    }

    const tabModel = (
        <Tab {...styles.tabStyle} headline={tabHeadLine} tabKey={productTabName}>
            <CardList extendedStyles={styles.cardList}>
                {productsToRender?.map(productContext => {
                    const product = productContext.product;
                    return (
                        <ProductContext.Provider value={productContext}>
                            <GridContainer
                                {...relatedProductAccordionStyles.gridContainer}
                                data-test-selector={`productListProductCard${product.id}`}
                            >
                                <GridItem {...relatedProductAccordionStyles.leftColumnGridItem} align="middle">
                                    <ProductImageWithModalView {...product} />
                                </GridItem>
                                <GridItem {...relatedProductAccordionStyles.rightColumnGridItem} align="middle">
                                    <GridContainer {...relatedProductAccordionStyles.rightColumnGridContainer}>
                                        <GridItem {...relatedProductAccordionStyles.infoGridItem}>
                                            <ProductDescription product={productContext} />
                                            <ProductPartNumbers
                                                productNumber={product.productNumber}
                                                customerProductNumber={product.customerProductNumber}
                                                manufacturerItem={product.manufacturerItem}
                                            />
                                            <ProductPrice
                                                product={productContext}
                                                showLabel={false}
                                                showSavings={true}
                                                showSavingsAmount={false}
                                                showSavingsPercent={false}
                                            />
                                        </GridItem>
                                        <GridItem {...relatedProductAccordionStyles.actionsGridItem}>
                                            <ProductListActions showPrice={false} showAddToList={false} />
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>
                            </GridContainer>
                        </ProductContext.Provider>
                    );
                })}
            </CardList>
            <GridContainer {...relatedProductAccordionStyles.moreLessGridContainer}>
                <GridItem
                    width={12}
                    css={css`
                        justify-content: center;
                    `}
                >
                    <Link
                        onClick={() => showFullListToggleFunction(!showFullList)}
                        icon={{
                            iconProps: { src: showFullList ? ChevronUp : ChevronDown },
                        }}
                    >
                        {showFullList ? translate("Less") : translate("More")}
                    </Link>
                </GridItem>
            </GridContainer>
        </Tab>
    );
    return tabModel;
}

function reorderSpecs(specsToShow: SpecificationModel[] | undefined) {
    const preferredSortOrder = ["Data Output", "Contents", "Logistics"];

    const specifications: SpecificationModel[] = [];
    preferredSortOrder.forEach(spec => {
        const isDisplayName = (s: SpecificationModel) => s.nameDisplay === spec;
        if (specsToShow?.some(isDisplayName)) {
            specifications.push(specsToShow?.filter(isDisplayName)[0]);
        }
    });

    return specifications;
}

const styles = specificationsStyles;

export interface ProductDetailSepcificationTabs {
    associatedWithTab?: JSX.Element;
    toolsUsedTab?: JSX.Element;
    substitutesTab?: JSX.Element;
    productResourcesTab?: JSX.Element;
    videosTab?: JSX.Element;
}

const ProductDetailsSpecificationsTabs: React.FC<Props> = ({
    id,
    product: { brand, attributeTypes, documents, specifications },
    pageType,
    fields,
    productSettings,
    relatedProducts,
    relatedProductContextList,
    relatedToolsContextList,
    relatedSubstitutesList,
    isLoaded,
    loadCarouselProducts,
    updateProductInfo,
}) => {
    const productContext = React.useContext(ProductContext);
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";

    const [showFullAssociatedWith, setShowFullAssociatedWith] = React.useState(false);
    const [showFullToolsUsed, setShowFullToolsUsed] = React.useState(false);
    const [showFullSubstitutes, setShowFullSubstitutes] = React.useState(false);

    React.useEffect(() => {
        loadCarouselProducts({
            carouselId: id,
            carouselType,
            relatedProductType: fields.relatedProductType,
            seedWithManuallyAssigned,
            displayProductsFrom,
            selectedCategoryIds,
            numberOfProductsToDisplay,
            isProductDetailsPage,
            productId: productContext?.product?.id,
            isProductListPage,
            isBrandDetailsPage,
        });
    }, [fields.relatedProductType, productContext?.product?.id]);

    React.useEffect(() => {
        loadCarouselProducts({
            carouselId: carouselTypeToolsUsedWidgetId,
            carouselType,
            relatedProductType: fields.toolsused,
            seedWithManuallyAssigned,
            displayProductsFrom,
            selectedCategoryIds,
            numberOfProductsToDisplay,
            isProductDetailsPage,
            productId: productContext?.product?.id,
            isProductListPage,
            isBrandDetailsPage,
        });
    }, [fields.toolsused, productContext?.product?.id]);

    React.useEffect(() => {
        loadCarouselProducts({
            carouselId: carouselTypeSubstitutesWidgetId,
            carouselType,
            relatedProductType: "substitute",
            seedWithManuallyAssigned,
            displayProductsFrom,
            selectedCategoryIds,
            numberOfProductsToDisplay,
            isProductDetailsPage,
            productId: productContext?.product?.id,
            isProductListPage,
            isBrandDetailsPage,
        });
    }, [fields.substitute, productContext?.product?.id]);

    if (!isLoaded) {
        return (
            <GridWrapper>
                <GridItem
                    width={12}
                    css={css`
                        justify-content: center;
                    `}
                >
                    <LoadingSpinner />
                </GridItem>
            </GridWrapper>
        );
    }

    const renderingDocuments = fields.showDocuments && documents && documents.length > 0;
    const renderingAttributes = fields.showAttributes && (brand || (attributeTypes && attributeTypes?.length > 0));
    const renderingSpecifications = specifications && specifications.length > 0;
    const renderingRelatedProducts = fields.showRelatedProducts && relatedProducts && relatedProducts.length > 0;

    const orderedExcludedSpecifications = `${fields.excludedSpecifications}`;

    const specsToExclude = orderedExcludedSpecifications?.split(",");
    const specsToShow = reorderSpecs(
        specsToExclude != null && specsToExclude.length > 0
            ? specifications?.filter(
                  spec => !specsToExclude.some((excludeSpec: string) => excludeSpec === spec.nameDisplay),
              )
            : specifications,
    );

    const getSection = (sectionName: string, specifications?: SpecificationModel[]) =>
        specifications?.filter(
            spec => !specsToExclude.some(s => s === sectionName) && spec.nameDisplay === sectionName,
        )[0];

    const relatedProductContextListConverted = relatedProductContextList?.map(
        productContext => ({
            ...productContext,
            onQtyOrderedChanged: (qtyOrdered: number) => updateProductInfo({ id, productId: productContext.product.id, qtyOrdered }),
            onUnitOfMeasureChanged: (unitOfMeasure: string) =>
                updateProductInfo({ id, productId: productContext.product.id, unitOfMeasure })
        })
    );

    tabs = [];
    currentTab = null;

    tabsModel.associatedWithTab = createProductTab(
        relatedProductContextListConverted,
        fields.relatedProductsTitle,
        "AssociatedProducts",
        showFullAssociatedWith,
        setShowFullAssociatedWith,
    );
    if (tabsModel.associatedWithTab != null) {
        tabs.push(tabsModel.associatedWithTab);
        currentTab = "AssociatedProducts";
    }

    tabsModel.toolsUsedTab = createProductTab(
        relatedToolsContextList,
        fields.toolsUsedTitle,
        "ToolsUsed",
        showFullToolsUsed,
        setShowFullToolsUsed,
    );
    if (tabsModel.toolsUsedTab != null) {
        tabs.push(tabsModel.toolsUsedTab);
        if (!currentTab) {
            currentTab = "ToolsUsed";
        }
    }

    tabsModel.substitutesTab = createProductTab(
        relatedSubstitutesList,
        fields.relatedSubstituteTitle,
        "Substitutes",
        showFullSubstitutes,
        setShowFullSubstitutes,
    );
    if (tabsModel.substitutesTab != null) {
        tabs.push(tabsModel.substitutesTab);
        if (!currentTab) {
            currentTab = "Substitutes";
        }
    }

    const productResourcesSection = renderingDocuments && (
        <Tab
            {...styles.tabStyle}
            headline={translate(fields.documentsTitle)}
            tabKey="ProductResources"
            data-test-selector="productDetails_specifications_documents"
        >
            <GridContainer {...styles.documentsContainer}>
                {documents!.map(document => (
                    <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                        <Link
                            href={document.filePath}
                            target="_new"
                            {...styles.documentLink}
                            data-test-selector={`productDetails_${document.id}`}
                        >
                            <IconMemo {...styles.documentIcon} />
                            <Typography {...styles.documentNameText}>{document.name}</Typography>
                        </Link>
                    </GridItem>
                ))}
            </GridContainer>
        </Tab>
    );
    if (productResourcesSection) {
        tabsModel.productResourcesTab = productResourcesSection;
        tabs.push(tabsModel.productResourcesTab);
    }

    const videosSection = getSection("Videos", specifications);
    if (videosSection && videosSection.htmlContent.length > 0) {
        const tabModel = (
            <Tab {...styles.tabStyle} headline="Videos" tabKey="Videos">
                {parse(videosSection.htmlContent, parserOptions)}
            </Tab>
        );
        tabsModel.videosTab = tabModel;
    }
    if (tabsModel.videosTab != null) {
        tabs.push(tabsModel.videosTab);
    }

    if (tabs.length <= 0) {
        return null;
    }

    if (!currentTab && (productResourcesSection || videosSection)) {
        currentTab = productResourcesSection ? "ProductResources" : videosSection ? "Videos" : "";
    }

    return <TabGroup current={currentTab ?? ""}>{tabs}</TabGroup>;
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(ProductDetailsSpecificationsTabs)),
    definition: {
        displayName: "Specification Tabs",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.documentsPosition,
                displayName: "Documents Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                fieldType: "General",
                sortOrder: 2,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showDocuments,
            },
            {
                name: fields.documentsTitle,
                displayName: "Documents Title",
                editorTemplate: "TextField",
                defaultValue: "Product Resources",
                fieldType: "General",
                isVisible: widget => widget.fields.showDocuments,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.attributesPosition,
                displayName: "Attributes Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "first",
                fieldType: "General",
                sortOrder: 4,
                options: [
                    {
                        displayName: "Display First",
                        value: "first",
                    },
                    {
                        displayName: "Display Last",
                        value: "last",
                    },
                ],
                isVisible: widget => widget.fields.showAttributes,
            },
            {
                name: fields.excludedSpecifications,
                displayName: "Excluded Specifications",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showRelatedProducts,
                displayName: "Show Related Products",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.relatedProductsTitle,
                displayName: "Related Products Title",
                editorTemplate: "TextField",
                defaultValue: "Related Products",
                fieldType: "Translatable",
                sortOrder: 7,
                isVisible: widget => widget.fields.showRelatedProducts,
            },
            {
                name: fields.relatedProductType,
                displayName: "Related Product Type",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 8,
                isVisible: widget => widget.fields.showRelatedProducts,
            },
            {
                name: fields.relatedToolsUsedTitle,
                displayName: "Tools Used Title",
                editorTemplate: "TextField",
                defaultValue: "Tools Used",
                fieldType: "Translatable",
                sortOrder: 9,
                isVisible: widget => widget.fields.showRelatedProducts,
            },
            {
                name: fields.toolsused,
                displayName: "Tools Used Type",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "toolsused",
                fieldType: "General",
                sortOrder: 10,
                isVisible: widget => widget.fields.showRelatedProducts,
            },
            {
                name: fields.relatedSubstituteTitle,
                displayName: "Substitutes",
                editorTemplate: "TextField",
                defaultValue: "Substitutes",
                fieldType: "Translatable",
                sortOrder: 11,
                isVisible: widget => widget.fields.showRelatedProducts,
            },
            {
                name: fields.substitute,
                displayName: "Substitutes Type",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "substitute",
                fieldType: "General",
                sortOrder: 12,
                isVisible: widget => widget.fields.showRelatedProducts,
            },
        ],
    },
};

export default widgetModule;

const ProductImageWithModalView: React.FC<ProductModel> = product => {
    const [fullPrimaryImageModalIsOpen, setFullImageModalIsOpen] = React.useState(false);
    const imageClickHandler = () => {
        setFullImageModalIsOpen(true);
    };
    const fullPrimaryImageModalCloseHandler = () => {
        setFullImageModalIsOpen(false);
    };

    const image = {
        id: product.id,
        imageAltText: product.imageAltText,
        imageType: "Static",
        largeImagePath: product.largeImagePath,
        mediumImagePath: product.mediumImagePath,
        smallImagePath: product.smallImagePath,
    } as ImageModel;
    return (
        <>
            <ProductPrimaryImage
                product={product}
                image={image}
                onClick={imageClickHandler}
                extendedStyles={primaryImageStyles.primaryImage}
            />
            <Modal
                {...primaryImageStyles.fullPrimaryImageModal}
                isOpen={fullPrimaryImageModalIsOpen}
                handleClose={fullPrimaryImageModalCloseHandler}
            >
                <ProductPrimaryImage
                    product={product}
                    image={image}
                    useLargeImage={true}
                    extendedStyles={primaryImageStyles.fullPrimaryImage}
                />
            </Modal>
        </>
    );
};
